import { useEffect, useRef, useState, useCallback } from "react";
import { Input, Box, TextInput } from "@mantine/core";
import { IconAlertTriangle } from "../../../icons";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/styles";
import styles from "./PhoneInput.module.css";
import { initialCountryLookup } from "./initialCountryLookup";

const PREFERRED_COUNTRY_ORDER = ["it", "gb", "us", "de", "fr", "es"];

const readPhoneValueWithoutUtils = (iti, inputEl) => {
    if (!iti || !inputEl) {
        return "";
    }

    const trimmed = inputEl.value.trim();
    if (!trimmed) {
        return "";
    }

    if (trimmed.startsWith("+")) {
        return trimmed;
    }

    const countryData = iti.getSelectedCountry();
    const dialCode = countryData?.dialCode ? `+${countryData.dialCode}` : "";
    return `${dialCode}${trimmed}`;
};

const readPhoneValue = (iti, inputEl) => {
    if (!iti) {
        return "";
    }

    if (intlTelInput.utils) {
        return iti.getNumber() || "";
    }

    return readPhoneValueWithoutUtils(iti, inputEl);
};

const buildIntlTelInputOptions = (countryCode, isPlain) => {
    const resolvedInitialCountry = countryCode?.toLowerCase();
    const options = {
        countryOrder: PREFERRED_COUNTRY_ORDER,
        // 'plain' hides the country selector and the separate dial code: the user types
        // the whole number. The selected country still drives the prefix that
        // readPhoneValue prepends, so the stored value keeps its format.
        separateDialCode: !isPlain,
        countrySelectorMode: isPlain ? "OFF" : "AUTO",
        showFlags: !isPlain,
        loadUtils: () => import("intl-tel-input/utils"),
        uiTranslations: { searchPlaceholder: "Search country code" },
        containerClass: isPlain ? styles.containerPlain : styles.container,
        dropdownParent: document.body,
    };

    if (resolvedInitialCountry) {
        options.initialCountry = resolvedInitialCountry;
    } else if (isPlain) {
        // With no country container intl-tel-input has no element to put the lookup
        // loading state on and throws (v29.0.5), so skip the lookup and start from the
        // first preferred country. The prefix still follows the country field.
        options.initialCountry = PREFERRED_COUNTRY_ORDER[0];
    } else {
        options.initialCountryLookup = initialCountryLookup;
    }

    return options;
};

export const PhoneInput = ({
    label,
    error,
    required,
    value,
    onChange,
    onBlur,
    placeholder,
    showLabel = true,
    countryCode,
    renderAs = "prefixed",
}) => {
    const isPlain = renderAs === "plain";
    const inputRef = useRef(null);
    const itiRef = useRef(null);
    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    const activatedRef = useRef(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        onBlurRef.current = onBlur;
    }, [onBlur]);

    const syncValue = useCallback(() => {
        if (!activatedRef.current || !itiRef.current || !inputRef.current) return;

        const fullNumber = readPhoneValue(itiRef.current, inputRef.current);
        onChangeRef.current?.(fullNumber || "");
    }, []);

    useEffect(() => {
        if (inputRef.current && !itiRef.current) {
            itiRef.current = intlTelInput(inputRef.current, buildIntlTelInputOptions(countryCode, isPlain));

            if (value) {
                itiRef.current.setNumber(value);
            }

            const handleCountryChange = () => syncValue();
            const handleCountrySelectorOpen = () => {
                activatedRef.current = true;
            };

            inputRef.current.addEventListener("countrychange", handleCountryChange);
            inputRef.current.addEventListener("open:countryselector", handleCountrySelectorOpen);

            return () => {
                if (inputRef.current) {
                    inputRef.current.removeEventListener("countrychange", handleCountryChange);
                    inputRef.current.removeEventListener("open:countryselector", handleCountrySelectorOpen);
                }
                if (itiRef.current) {
                    itiRef.current.destroy();
                    itiRef.current = null;
                }
            };
        }
    }, [syncValue]);

    // Update when value changes externally — wait for utils before calling getNumber()
    useEffect(() => {
        const iti = itiRef.current;
        if (!iti || value === undefined) return;

        let cancelled = false;

        const applyExternalValue = () => {
            if (cancelled || itiRef.current !== iti) return;

            const currentNumber = readPhoneValue(iti, inputRef.current);
            if (currentNumber !== value) {
                iti.setNumber(value || "");
            }
        };

        if (intlTelInput.utils) {
            applyExternalValue();
            return () => {
                cancelled = true;
            };
        }

        iti.promise.then(applyExternalValue).catch(applyExternalValue);

        return () => {
            cancelled = true;
        };
    }, [value]);

    // Sync country from CountrySelect field — only when phone value is empty
    useEffect(() => {
        if (!itiRef.current || !countryCode || value) return;
        const current = itiRef.current.getSelectedCountry();
        if (current?.iso2?.toUpperCase() === countryCode.toUpperCase()) return;
        try {
            itiRef.current.setSelectedCountry(countryCode.toLowerCase());
        } catch {
            // Country not in intl-tel-input list — keep current prefix
        }
    }, [countryCode, value]);

    const handleInput = () => {
        activatedRef.current = true;
        syncValue();
    };

    const handleFocus = () => {
        activatedRef.current = true;
        setIsFocused(true);
    };

    const handleFieldBlur = () => {
        setIsFocused(false);
        syncValue();
        onBlurRef.current?.();
    };

    const hasValue = value && value.length > 0;

    return (
        <Box className={styles.wrapper} data-has-value={isFocused || hasValue ? true : undefined}>
            <Input.Wrapper
                label={showLabel ? label : undefined}
                error={error}
                required={required}
                withAsterisk={showLabel && required}
                classNames={{
                    label: `${styles.label} ${isPlain ? styles.labelPlain : ""}`,
                }}
            >
                {/* Both variants render a Mantine field, so they follow the theme.
                    intl-tel-input overlays the country selector on the left and sets the
                    input padding itself when the variant is 'prefixed'. */}
                <TextInput
                    ref={inputRef}
                    type="tel"
                    placeholder={placeholder || ""}
                    error={error}
                    rightSection={
                        error ? (
                            <IconAlertTriangle size={18} stroke={1.5} className="input-error-icon" />
                        ) : null
                    }
                    onInput={handleInput}
                    onFocus={handleFocus}
                    onBlur={handleFieldBlur}
                />
            </Input.Wrapper>
        </Box>
    );
};
